/**
 * <scroll-to-top-btn> — floating button shown once the page has scrolled past a
 * configurable offset. Visibility is driven by an IntersectionObserver watching a
 * sentinel placed in the document flow, rather than a scroll listener.
 */
class ScrollToTopBtn extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.scroll-to-top-btn__button');
    this.displayAfter = Number(this.dataset.displayAfter) || 0;
    this.hideTimeoutId = null;

    this.button.addEventListener('click', () => this.scrollToTop());

    // display_after of 0 means "always visible": no sentinel/observer needed.
    if (this.displayAfter > 0) this.setupVisibilityObserver();
  }

  disconnectedCallback() {
    window.clearTimeout(this.hideTimeoutId);
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.remove();
  }

  setupVisibilityObserver() {
    this.sentinel = document.createElement('div');
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.sentinel.style.cssText = `position:absolute; top:${this.displayAfter}px; left:0; width:1px; height:1px; pointer-events:none;`;
    document.body.appendChild(this.sentinel);

    this.observer = new IntersectionObserver((entries) => this.toggleVisibility(!entries[0].isIntersecting));
    this.observer.observe(this.sentinel);
  }

  toggleVisibility(isPastThreshold) {
    window.clearTimeout(this.hideTimeoutId);

    if (isPastThreshold) {
      this.removeAttribute('hidden');
      this.classList.remove('scroll-to-top-btn--is-leaving');
      return;
    }

    this.classList.add('scroll-to-top-btn--is-leaving');
    // Keep the button in the layout for the fade-out transition, then hide it.
    this.hideTimeoutId = window.setTimeout(() => {
      this.setAttribute('hidden', '');
    }, 200);
  }

  scrollToTop() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }
}

customElements.define('scroll-to-top-btn', ScrollToTopBtn);
